import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { catchError, map, Observable, of, switchMap, take } from 'rxjs';
import { EnvironmentService } from './environment.service';
import { SKIP_CACHE_HEADER } from './interceptor';
import { saveAs } from 'file-saver';
import { FileFolderType } from '../types';

@Injectable({
  providedIn: 'root',
})
export class BaseService {
  get apiUrl(): string {
    return this.environmentService.environments.apiUrl;
  }

  protected http: HttpClient = inject(HttpClient);
  protected environmentService: EnvironmentService = inject(EnvironmentService);

  private buildUrl(uri: string): string {
    return uri.startsWith('http') ? uri : this.apiUrl + uri;
  }

  getFile(uri: string, queryParams: any, responseType?: any, observe?: any): Observable<any> {
    const params = this.getUrlParams(this.normalBody(queryParams));
    return this.http.get(this.buildUrl(uri), {
      params,
      responseType,
      observe,
    });
  }

  request<T = any>(
    method: string,
    uri: string,
    queryParams?: unknown,
    responseType?: any,
    caching?: boolean,
  ): Observable<T> {
    const headers = this.getRequestHeader(caching);
    const params = this.getUrlParams(this.normalBody(queryParams));
    return this.http.request<T>(method, this.buildUrl(uri), {
      params,
      responseType,
      headers,
    });
  }

  get<T = any>(
    uri: string,
    queryParams?: unknown,
    responseType?: any,
    caching?: boolean,
  ): Observable<T> {
    const headers = this.getRequestHeader(caching);
    const params = this.getUrlParams(this.normalBody(queryParams));
    return this.http.get<T>(this.buildUrl(uri), {
      params,
      responseType,
      headers,
    });
  }

  post<T = any>(uri: string, body?: any, options?: any): Observable<T> {
    body = this.normalBody(body);
    return this.http.post(this.buildUrl(uri), body, options) as Observable<T>;
  }

  delete<T>(uri: string, body?: any): Observable<T> {
    body = this.normalBody(body);
    return this.http.delete<T>(this.buildUrl(uri), {
      body,
    }) as Observable<T>;
  }

  put<T>(uri: string, body?: any): Observable<T> {
    body = this.normalBody(body);
    return this.http.put<T>(this.buildUrl(uri), body) as Observable<T>;
  }

  patch<T>(uri: string, body?: any): Observable<T> {
    body = this.normalBody(body);
    return this.http.patch<T>(this.buildUrl(uri), body) as Observable<T>;
  }

  uploadFile(formData: FormData, folderType?: FileFolderType): Observable<string> {
    return this.post('File', formData, {
      params: { folderType },
    }).pipe(map(response => response.value)); // uploaded file path
  }

  downloadByUrl(url: string, filter?: any, filename?: string) {
    return this.get(url, filter, 'blob').pipe(
      switchMap((response: any) => {
        if (response) {
          saveAs(response, filename ?? 'File');
          return of(true);
        }
        return of(false);
      }),
      catchError(() => {
        throw new Error('base.failedDownload');
      }),
    );
  }

  downloadCsvByUrl(url: string, filter?: any, filename?: string) {
    const headers = new HttpHeaders({
      Accept: 'text/csv',
    });

    return this.post(url, filter, {
      responseType: 'blob',
      headers: headers,
    }).pipe(
      switchMap((response: Blob) => {
        if (response && response.size > 0) {
          if (response.type.includes('text/csv')) {
            const finalFileName = filename ? filename : 'File.csv';
            saveAs(response, finalFileName);
            return of(true);
          } else {
            console.warn('Expected CSV but got:', response.type);
          }
        }
        return of(false);
      }),
      catchError(() => {
        throw new Error('base.failedDownload');
      }),
    );
  }

  downloadFile(filename: string, onSuccess?: () => void, onError?: (error?: Error) => void): void {
    this.get(
      'File/download',
      {
        filePath: filename,
      },
      'blob',
    )
      .pipe(
        take(1),
        catchError(error => {
          if (onError) {
            onError(error);
          }
          throw new Error('base.failedDownload');
        }),
      )
      .subscribe((response: any) => {
        // Use file-saver to save the blob as a file
        saveAs(response, filename);
        if (onSuccess) {
          onSuccess();
        }
      });
  }

  downloadStaticFile(file: File): void {
    saveAs(file, file.name);
  }

  private getRequestHeader(caching?: boolean): HttpHeaders {
    if (!caching) {
      return new HttpHeaders().set(SKIP_CACHE_HEADER, 'true');
    }
    return new HttpHeaders();
  }

  private normalBody(body: any): any {
    if (!body) {
      body = {};
    }
    for (const key in body) {
      if (!Object.prototype.hasOwnProperty.call(body, key)) {
        continue;
      }
      if (body[key] instanceof Date) {
        body[key] = this.formatDate(body[key]);
      }
    }
    return body;
  }

  formatDate(date: any): string {
    // TODO: Date format
    // return moment(date).format('YYYY-MM-DD[T]HH:mm:ss');
    return date;
  }

  getUrlParams(body: any): HttpParams {
    let params = new HttpParams();
    for (const key in body) {
      if (
        !Object.prototype.hasOwnProperty.call(body, key) ||
        body[key] === null ||
        body[key] === undefined
      ) {
        continue;
      }
      if (Array.isArray(body[key])) {
        const arr: any[] = body[key];
        arr.forEach(value => (params = params.append(key, value)));
        continue;
      }
      params = params.append(key, body[key]);
    }
    return params;
  }
}
